// Three files, because the country data is big and not everyone can wait for
// it:
//
//   country-lookup  the rules. No data, so it is safe to import anywhere.
//   country         loads the data up front, for callers that need an answer
//                   while rendering.
//   country-async   loads it only when called, so pages that do not need it
//                   right away do not ship it (#2751).
//
// Keep the rules in country-lookup. When they lived in the other two, a fix
// went into one copy and shipped doing nothing.
//
// This file loads the data up front.

import { AFRICAN_COUNTRIES, IS_COUNTRIES } from '@common/constants'
import { USER_COUNTRY_FROM_IP_COOKIE } from '@lib/constants/common'
import { SIGNUP_BLOCKED_COUNTRY_CODES } from '@lib/data/country'
import {
  countryCodeFromLocale,
  findCountryByName,
  pickCountryForTimezone,
} from '@lib/utils/country-lookup'
import {
  Country,
  getAllCountries,
  getCountriesForTimezone,
  getCountry,
} from 'countries-and-timezones'
import { parseCookies } from 'nookies'

// Undefined when the name is not a country. It used to return an empty
// country object, which is truthy, so callers checking "did we find one?"
// thought they had and stopped looking.
export const getCountryFromCountryName = (
  countryName: string
): Country | undefined => findCountryByName(getAllCountries(), countryName)

/**
 * True only when the country name positively resolves to a country in the
 * Indian subcontinent or Africa - the region the backend calls ISA.
 *
 * Mirrors `is_isa_country` in `api/apps/common/country_info.py`, so a gate
 * that runs on both sides cannot disagree about who is in the region. An
 * unknown or unmappable name returns false, so region-gated friction fails
 * open rather than being shown to someone we cannot place.
 */
export const isIsaCountryName = (countryName?: string): boolean => {
  if (!countryName) return false
  const countryCode = getCountryFromCountryName(countryName)?.id
  return (
    !!countryCode &&
    (IS_COUNTRIES.has(countryCode) || AFRICAN_COUNTRIES.has(countryCode))
  )
}

export const getCountryFromLocale = (locale: string) =>
  getCountry(countryCodeFromLocale(locale))

export const isCountryBlockedForSignup = (): boolean => {
  const countryCodeViaCookie = parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE]
  return (
    !!countryCodeViaCookie &&
    SIGNUP_BLOCKED_COUNTRY_CODES.includes(countryCodeViaCookie.toUpperCase())
  )
}

export const getCountryForTimezone = (timezone: string) =>
  pickCountryForTimezone(
    getCountriesForTimezone(timezone),
    getCountry(parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE])
  )
